import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Icon from '../../../components/Icon';
import type {
  PaymentKey,
  PaymentSebStatus,
  PaymentStatus,
} from '../../../client/portal/types.gen';
import { useNotification } from '../../../context/notificationContext';
import { GridTableCell } from '../../../components/table/GridTableCell';

import FulfillPayment from './FulfillPayment';
import { useAlert } from '../../../context/alertContext';
import { useOverlay } from '../../../context/overlayContext';
import {
  removePaymentService,
  signPaymentService,
} from '../../../services/portalServices';
import PaymentStatusHistory from './PaymentStatusHistory';
import IconButton from '../../../components/IconButton';
import type { PaymentTableRowContent } from './PaymentsTable';

type PaymentStatusTag = PaymentStatus['tag'];
type PaymentSebStatusTag = PaymentSebStatus['tag'];

type PaymentTableProps = {
  content: PaymentTableRowContent;
  getPayments?: () => Promise<void>;
  onDeselectPayment?: (paymentKey: PaymentKey) => void;
  onSelectPayment?: (paymentKey: PaymentKey) => void;
};

const PaymentTableRowNew = ({
  content,
  getPayments,
  onSelectPayment,
  onDeselectPayment,
}: PaymentTableProps) => {
  const { setAlert } = useAlert();
  const { addNotification, removeNotificationWithDelay } = useNotification();
  const { setChildNodes } = useOverlay();

  const [isPosting, setIsPosting] = useState<boolean>(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState<boolean>(false);

  const handleShowPaymentHistoryClick = () => setShowPaymentHistory(true);
  const hidePaymentHistory = () => setShowPaymentHistory(false);

  const handleFulfillPaymentClick = (): void => {
    setChildNodes(
      <FulfillPayment
        partnerApiUrl={content.partnerApiUrl}
        claimcaseKey={content.claimcaseKey}
        claimKey={content.claimKey}
        paymentKey={content.paymentKey}
        getPayments={getPayments ?? (() => {})}
      />,
    );
  };

  const handleSignPaymentClick = async () => {
    setIsPosting(true);

    try {
      await signPaymentService(
        content.partnerApiUrl,
        content.claimcaseKey,
        content.claimKey,
        content.paymentKey,
      );

      removeNotificationWithDelay(
        addNotification('The payment has been signed.', 'success'),
      );
    } catch (error) {
      removeNotificationWithDelay(
        addNotification((error as { message: string }).message, 'alert'),
      );
    } finally {
      getPayments && getPayments();
      setIsPosting(false);
    }
  };

  const ActionButton = (status: PaymentStatusTag) => {
    switch (status) {
      case 'PaymentScheduled':
        return (
          <IconButton
            options={{
              color: '#42c67f',
              hoverColor: '#34ad6c',
              size: 'small',
            }}
            onClick={handleFulfillPaymentClick}
          >
            <Icon iconName='faCheck' />
          </IconButton>
        );
      case 'PaymentUnsigned':
        return (
          <IconButton
            disabled={isPosting}
            options={{
              color: '#42c67f',
              hoverColor: '#34ad6c',
              size: 'small',
            }}
            onClick={handleSignPaymentClick}
          >
            <Icon iconName='faFileSignature' />
          </IconButton>
        );
      default:
        return null;
    }
  };

  const handleRemoveRejectedPayment = async () => {
    setIsPosting(true);
    try {
      await removePaymentService(
        content.partnerApiUrl,
        content.claimcaseKey,
        content.claimKey,
        content.paymentKey,
      );

      removeNotificationWithDelay(
        addNotification('The payment has been removed.', 'success'),
      );
    } catch (error) {
      removeNotificationWithDelay(
        addNotification((error as { message: string }).message, 'alert'),
      );
    } finally {
      getPayments && getPayments();
      setIsPosting(false);
    }
  };

  const handleRemovePaymentClick = async () => {
    setAlert({
      body: {
        header: 'Remove payment?',
        message: `Do you want to remove the payment of ${content.amount.toLocaleString(
          'sv-SE',
        )} in claim case ${content.claimcaseKey}? The action cannot be undone.`,
      },
      buttons: {
        Cancel: {
          type: 'secondary',
          text: 'Cancel',
          action() {
            setAlert(null);
          },
        },
        Ok: {
          type: 'primary',
          text: 'Remove payment',
          action: async function () {
            try {
              await removePaymentService(
                content.partnerApiUrl,
                content.claimcaseKey,
                content.claimKey,
                content.paymentKey,
              );

              getPayments && getPayments();

              setAlert({
                body: {
                  header: 'Payment removed',
                  message: 'The payment was successfully removed.',
                },
                buttons: {
                  Ok: {
                    type: 'primary',
                    text: 'OK',
                    action() {
                      setAlert(null);
                    },
                  },
                },
                type: 'success',
              });
            } catch (error) {
              setAlert({
                body: {
                  header: 'Something went wrong',
                  message: (error as { message: string }).message,
                },
                buttons: {
                  Ok: {
                    type: 'primary',
                    text: 'OK',
                    action() {
                      setAlert(null);
                    },
                  },
                },
                type: 'warning',
              });
            }
          },
        },
      },
      type: 'warning',
    });
  };

  const RemoveButton = (status: PaymentStatusTag | PaymentSebStatusTag) => {
    switch (status) {
      case 'PaymentFulfilled':
      case 'PaymentSebRejected':
      case 'PaymentScheduled':
      case 'PaymentUnsigned':
      case 'PaymentSebPending':
      case 'PaymentSebAcknowledged':
        return (
          <IconButton
            disabled={isPosting}
            options={{
              color: '#ff6f6f',
              hoverColor: '#ff4747',
              size: 'small',
            }}
            onClick={
              status === 'PaymentSebRejected'
                ? handleRemoveRejectedPayment
                : handleRemovePaymentClick
            }
          >
            <Icon iconName='faTrashAlt' />
          </IconButton>
        );

      // Booked and fulfilled payments have gone through, and a removed one is
      // already gone: nothing to remove.
      case 'PaymentSeb':
      case 'PaymentSebBooked':
      case 'PaymentSebFulfilled':
      case 'PaymentSebRemoved':
        return null;
    }
  };

  const handleCheckboxClick = () => {
    content.selected
      ? onDeselectPayment && onDeselectPayment(content.paymentKey)
      : onSelectPayment && onSelectPayment(content.paymentKey);
  };

  return (
    <>
      {content.status.tag === 'PaymentScheduled' && (
        <TableCell>
          {content.hasPaymentIntegration && (
            <input
              type='checkbox'
              checked={content.selected}
              onChange={handleCheckboxClick}
              // disabled={isPosting || !content.hasPaymentIntegration}
            />
          )}
        </TableCell>
      )}
      <HideOverflowCell title={content.partner}>
        {content.partner}
      </HideOverflowCell>
      <TableCell>
        <Link
          to={`/partner?url=${decodeURIComponent(
            content.frontendBaseUrl,
          )}&redirect=${decodeURIComponent(
            `/claim-cases/${content.claimcaseKey}`,
          )}`}
        >
          {content.claimcaseKey}
        </Link>
      </TableCell>
      <HideOverflowCell title={content.method}>
        {content.method}
      </HideOverflowCell>
      <HideOverflowCell title={content.paymentRecipient}>
        {content.paymentRecipient}
      </HideOverflowCell>
      <HideOverflowCell title={content.message}>
        {content.message}
      </HideOverflowCell>
      <TableCell>
        <NoWrap>{content.dueDate}</NoWrap>
      </TableCell>
      {content.status.tag === 'PaymentSeb' &&
        content.status.paymentSebStatus.tag === 'PaymentSebRejected' && (
          <TableCell>{content.rejectedReason}</TableCell>
        )}
      <HideOverflowCell title={content.requestedBy}>
        {content.requestedBy}
      </HideOverflowCell>
      <HideOverflowCell title={content.signedBy}>
        {content.signedBy}
      </HideOverflowCell>
      <TableCell>{content.currency}</TableCell>
      <TableCell textAlign='right'>
        {content.amount.toLocaleString('sv-SE')}
      </TableCell>
      <TableCell>
        <ActionButtonsWrapper>
          {ActionButton(content.status.tag)}
          {content.status.tag === 'PaymentSeb'
            ? RemoveButton(content.status.paymentSebStatus.tag)
            : RemoveButton(content.status.tag)}
          <IconButton
            options={{
              size: 'small',
            }}
            onClick={handleShowPaymentHistoryClick}
          >
            <Icon iconName='faClockRotateLeft' />
          </IconButton>
          {showPaymentHistory && (
            <PaymentStatusHistory
              payment={content.payment.payment}
              hide={hidePaymentHistory}
            />
          )}
        </ActionButtonsWrapper>
      </TableCell>
    </>
  );
};

export default PaymentTableRowNew;

const TableCell = styled(GridTableCell)<{
  isHovering?: boolean;
}>`
  color: ${({ isHovering }) => (isHovering ? '#fc8e49' : 'inherit')};
  text-decoration: ${({ isHovering }) => (isHovering ? 'underline' : 'none')};
`;

const ActionButtonsWrapper = styled.div`
  display: flex;
  gap: 0.125rem;
  position: relative;
`;

const NoWrap = styled.span`
  white-space: nowrap;
`;

const HideOverflowCell = styled(GridTableCell)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
